<template>
    <b-card 
        class="fraud-card shadow-sm p-3 mb-4" 
        :class="{ 'marked-safe': isMarkedSafe }"
        :style="{ '--border-color': isMarkedSafe ? '#28a745' : alert.borderColor }"
    >

        <!-- TOP SECTION -->
        <div class="card-top-section">
            
            <!-- LEFT -->
            <div class="left-col">
                <h5 class="mb-1 card-id">{{ alert.id }}</h5>

                <!-- BADGES -->
                <div class="badge-wrapper">
                    <b-badge :variant="alert.severityVariant" class="mr-1 mb-1">
                        {{ alert.severity }}
                    </b-badge>

                    <b-badge v-if="!isMarkedSafe" variant="primary" class="mr-1 mb-1">Active</b-badge>
                    <b-badge v-if="isMarkedSafe" variant="success" class="mr-1 mb-1">
                        <i class="fa fa-check-circle mr-1"></i>Marked Safe
                    </b-badge>
                    <b-badge v-if="alert.isBlocked" variant="dark" class="mr-1 mb-1">
                        <i class="fa fa-ban mr-1"></i>Blocked
                    </b-badge>
                    <b-badge variant="info" class="mb-1">Auto-Detected</b-badge>
                </div>

                <!-- TITLE -->
                <p class="mt-2 mb-1 font-weight-bold card-title">{{ alert.title }}</p>
                <p class="text-muted mb-1">User: {{ alert.user }}</p>

                <!-- MESSAGE BOX -->
                <div class="alert-message">
                    {{ alert.message }}
                </div>

                <!-- RELATED ORDERS -->
                <p class="mt-3 mb-1 font-weight-bold">Related Orders:</p>
                <div class="order-badges-wrapper">
                    <b-badge v-for="o in alert.orders" :key="o" class="order-badge mr-1 mb-1">
                        {{ o }}
                    </b-badge>
                </div>
            </div>

            <!-- RIGHT -->
            <div class="right-col">
                <small>{{ alert.time }}</small>
                <p class="text-danger mt-1 risk-info">
                    <i class="fas fa-shield-alt mr-1"></i>
                    Risk: {{ alert.risk }}/100
                </p>
            </div>
        </div>

        <!-- ACTION BUTTONS -->
        <div class="mt-3 d-flex flex-wrap action-row">
            <!-- <b-button variant="primary" class="mr-2 btn-sm">Investigate</b-button> -->
            <b-button 
                v-if="!isMarkedSafe && !alert.isBlocked"
                variant="success" 
                class="mr-2 btn-sm cursor-pointer"
                @click="markAsSafe(alert)"
            >
                <i class="fa fa-check-circle mr-1"></i>Mark as Safe
            </b-button>
            <b-button 
                variant="danger" 
                class="mr-2 btn-sm cursor-pointer"
                :disabled="isMarkedSafe || alert.isBlocked"
                @click="blockUser(alert.customer_id, alert.fraud_source)"
            >
                <i class="fa fa-ban mr-1"></i>{{ alert.isBlocked ? 'Blocked' : 'Block User' }}
            </b-button>
            <!-- <b-button variant="dark" class=" mr-2 btn-sm">Assign to Team</b-button> -->
        </div>

    </b-card>
</template>

<script>
export default {
    name: "FraudAlertCard",
    props: {
        alert: Object,
    },
    data() {
        return {
            isMarkedSafe: false,
        };
    },
    mounted() {
        this.checkMarkedSafeStatus();
    },
    methods: {
        checkMarkedSafeStatus() {
            const storageKey = `fraud_alert_safe_${this.alert.id}`;
            const markedSafe = localStorage.getItem(storageKey);
            this.isMarkedSafe = markedSafe === "true";
        },
        markAsSafe(alert) {
            const storageKey = `fraud_alert_safe_${alert.id}`;
            localStorage.setItem(storageKey, "true");
            this.isMarkedSafe = true;
            
            // Store additional info for reference
            const safeInfo = {
                alertId: alert.id,
                userId: alert.user,
                markedAt: new Date().toISOString(),
                alertTitle: alert.title,
            };
            localStorage.setItem(`${storageKey}_info`, JSON.stringify(safeInfo));
            
            this.$bvToast.toast("Alert marked as safe. User will not be blocked.", {
                title: "Success",
                variant: "success",
                solid: true,
                autoHideDelay: 3000,
            });
        },
        async blockUser(id, fraudSource) {
            const action =
                fraudSource === "CUSTOMER"
                    ? "onboardCustomer/updateCustomer"
                    : "partner/partnerStatusUpdate";
            await this.$store
                .dispatch(action, {
                    id: id,
                    data: {
                        status: "INACTIVE",
                    },
                })
                .then((res) => {
                    this.$bvToast.toast("User blocked successfully", {
                        title: "Success",
                        variant: "success",
                        solid: true,
                        autoHideDelay: 3000,
                    });
                    this.$emit("user-blocked");
                })
                .catch((err) => {
                    this.$bvToast.toast("Failed to block user", {
                        title: "Error",
                        variant: "danger",
                        solid: true,
                        autoHideDelay: 3000,
                    });
                });
        },  
    },
};
</script>

<style scoped>
.fraud-card {
    border-radius: 12px;
    border-left: 4px solid var(--border-color, #e74c3c);
    max-width: 100%;
    overflow-wrap: break-word;
}

.card-top-section {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
}

.left-col {
    flex: 1 1 auto;
    min-width: 0;
}

.right-col {
    flex: 0 0 auto;
    text-align: right;
}

.card-id {
    font-size: clamp(14px, 2vw, 18px);
    word-break: break-all;
}

.card-title {
    word-break: break-word;
}

.badge-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
}

.badge-wrapper .badge {
    white-space: nowrap;
}

.order-badges-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
}

.alert-message {
    background: #fafafa;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid #eee;
    font-size: clamp(13px, 1.8vw, 14px);
    word-break: break-word;
    overflow-wrap: break-word;
}

.order-badge {
    background: #f3f4f6;
    color: #555;
    padding: 5px 10px;
    border-radius: 6px;
    font-size: 12px;
    word-break: break-all;
}

.action-row {
    gap: 0.5rem;
}

.action-row .btn-sm {
    padding: 6px 14px;
    font-size: clamp(12px, 1.8vw, 14px);
    transition: all 0.2s ease;
}

.action-row .btn-sm:disabled {
    opacity: 0.5;
    cursor: not-allowed !important;
    background-color: #6c757d !important;
    border-color: #6c757d !important;
}

.fraud-card.marked-safe {
    border-left-color: #28a745;
    background-color: #f8fff9;
}

.right-col small {
    font-size: 13px;
}

.left-col h5 {
    font-weight: 600;
}

@media (max-width: 767.98px) {
    .fraud-card {
        padding: 1rem !important;
        margin-left: 0.25rem;
        margin-right: 0.25rem;
    }
    
    .card-top-section {
        flex-direction: column;
    }
    
    .right-col {
        text-align: left !important;
        margin-top: 0;
        padding-top: 0.75rem;
        border-top: 1px solid #eee;
    }
    
    .action-row .btn-sm {
        flex: 1 1 calc(50% - 0.5rem);
        min-width: 120px;
    }
}

@media (max-width: 575.98px) {
    .fraud-card {
        padding: 0.75rem !important;
    }
    
    .action-row .btn-sm {
        flex: 1 1 100%;
        min-width: 100%;
    }
    
    .alert-message {
        padding: 10px;
    }
}
</style>
